import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Paper, Grid, Select, MenuItem, FormControl, InputLabel,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { fetchData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const STATUS_COLORS = {
  Present: { bg: "#e8f5e9", text: "#4caf50", border: "#a5d6a7" },
  Absent:  { bg: "#fdecea", text: "#f44336", border: "#ef9a9a" },
  Leave:   { bg: "#fff3e0", text: "#ff9800", border: "#ffcc80" },
  Holiday: { bg: "#e3f2fd", text: "#2196f3", border: "#90caf9" },
};

// Build fake attendance data for a full month
const buildFallbackAttendance = (year, month) => {
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const records = {};
  for (let d = 1; d <= daysInMonth; d++) {
    const date = new Date(year, month, d);
    const dow = date.getDay();
    if (dow === 0 || dow === 6) continue; // skip weekends
    const rand = Math.random();
    let status;
    if (rand < 0.80) status = "Present";
    else if (rand < 0.90) status = "Absent";
    else status = "Leave";
    records[d] = status;
  }
  return records;
};

const ParentAttendance = () => {
  const today = new Date();
  const [selectedMonth, setSelectedMonth] = useState(today.getMonth());
  const [selectedYear, setSelectedYear] = useState(today.getFullYear());
  const [attendanceMap, setAttendanceMap] = useState({});
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const years = [];
  for (let y = today.getFullYear(); y >= today.getFullYear() - 2; y--) years.push(y);

  const loadAttendance = useCallback(async () => {
    try {
      const res = await fetchData(
        `parent/attendance?month=${selectedMonth + 1}&year=${selectedYear}`
      );
      if (res?.data) {
        // Expect res.data as array of { date, status } or object { day: status }
        if (Array.isArray(res.data)) {
          const map = {};
          res.data.forEach((rec) => {
            const day = new Date(rec.date).getDate();
            map[day] = rec.status;
          });
          setAttendanceMap(map);
        } else if (typeof res.data === "object") {
          setAttendanceMap(res.data);
        } else {
          setAttendanceMap(buildFallbackAttendance(selectedYear, selectedMonth));
        }
      } else {
        setAttendanceMap(buildFallbackAttendance(selectedYear, selectedMonth));
      }
    } catch (_) {
      setAttendanceMap(buildFallbackAttendance(selectedYear, selectedMonth));
    }
  }, [selectedMonth, selectedYear]);

  useEffect(() => { loadAttendance(); }, [loadAttendance]);

  // Calendar helpers
  const daysInMonth = new Date(selectedYear, selectedMonth + 1, 0).getDate();
  const firstDayOfMonth = new Date(selectedYear, selectedMonth, 1).getDay(); // 0=Sun

  // Stats
  let totalDays = 0, present = 0, absent = 0, leave = 0;
  for (let d = 1; d <= daysInMonth; d++) {
    const dow = new Date(selectedYear, selectedMonth, d).getDay();
    if (dow === 0 || dow === 6) continue;
    totalDays++;
    const status = attendanceMap[d];
    if (status === "Present") present++;
    else if (status === "Absent") absent++;
    else if (status === "Leave") leave++;
  }
  const attendancePct = totalDays > 0 ? Math.round((present / totalDays) * 100) : 0;
  const leavePct = totalDays > 0 ? Math.round((leave / totalDays) * 100) : 0;

  const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  // Build calendar grid cells
  const calendarCells = [];
  for (let i = 0; i < firstDayOfMonth; i++) calendarCells.push(null);
  for (let d = 1; d <= daysInMonth; d++) calendarCells.push(d);

  const isToday = (d) =>
    d === today.getDate() && selectedMonth === today.getMonth() && selectedYear === today.getFullYear();

  const isWeekend = (d) => {
    if (!d) return false;
    const dow = new Date(selectedYear, selectedMonth, d).getDay();
    return dow === 0 || dow === 6;
  };

  return (
    <Box sx={{ textAlign: "left", padding: "1rem", fontFamily: "'Inter', 'Roboto', sans-serif" }}>
      <Toast
        open={toast.open}
        handleClose={() => setToast({ ...toast, open: false })}
        severity={toast.severity}
        message={toast.message}
      />

      <Typography sx={{ color: "rgba(0,0,0,0.45)", fontWeight: "bold", fontSize: "14px", mb: 1.5 }}>
        Pages / Attendance
      </Typography>

      {/* Header + Filters */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography sx={{ fontWeight: "bold", fontSize: "22px", color: "#1a1a2e" }}>
            Attendance Calendar
          </Typography>
          <Typography sx={{ color: "#8F95B2", fontSize: "13px", mt: 0.3 }}>
            {MONTHS[selectedMonth]} {selectedYear}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel sx={{ fontSize: "13px" }}>Month</InputLabel>
            <Select
              value={selectedMonth}
              label="Month"
              onChange={(e) => setSelectedMonth(e.target.value)}
              sx={{ fontSize: "13px", borderRadius: "8px" }}
            >
              {MONTHS.map((m, i) => (
                <MenuItem key={m} value={i} sx={{ fontSize: "13px" }}>{m}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel sx={{ fontSize: "13px" }}>Year</InputLabel>
            <Select
              value={selectedYear}
              label="Year"
              onChange={(e) => setSelectedYear(e.target.value)}
              sx={{ fontSize: "13px", borderRadius: "8px" }}
            >
              {years.map((y) => (
                <MenuItem key={y} value={y} sx={{ fontSize: "13px" }}>{y}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Summary Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: "Total School Days", value: totalDays, color: "#244C77", bg: "#EDE9FB", icon: <CalendarMonthIcon sx={{ fontSize: "22px" }} /> },
          { label: "Present", value: present, color: "#4caf50", bg: "#e8f5e9", icon: <CheckCircleIcon sx={{ fontSize: "22px" }} /> },
          { label: "Absent", value: absent, color: "#f44336", bg: "#fdecea", icon: <CancelIcon sx={{ fontSize: "22px" }} /> },
          { label: "Leave", value: leave, color: "#ff9800", bg: "#fff3e0", icon: <EventBusyIcon sx={{ fontSize: "22px" }} /> },
        ].map((stat) => (
          <Grid item xs={6} sm={3} key={stat.label}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                p: 2.5,
                display: "flex",
                alignItems: "center",
                gap: 2,
                boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
                backgroundColor: "#fff",
              }}
            >
              <Box
                sx={{
                  width: 46,
                  height: 46,
                  borderRadius: "12px",
                  backgroundColor: stat.bg,
                  color: stat.color,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {stat.icon}
              </Box>
              <Box>
                <Typography sx={{ fontSize: "22px", fontWeight: "bold", color: "#1a1a2e", lineHeight: 1.2 }}>
                  {stat.value}
                </Typography>
                <Typography sx={{ fontSize: "11px", color: "#8F95B2", mt: 0.2 }}>{stat.label}</Typography>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Attendance rate bar */}
      <Paper elevation={0} sx={{ borderRadius: 3, p: 2.5, boxShadow: "0 2px 10px rgba(0,0,0,0.06)", mb: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#374151" }}>Attendance Rate</Typography>
          <Typography sx={{ fontSize: "14px", fontWeight: 700, color: attendancePct >= 75 ? "#4caf50" : "#f44336" }}>
            {attendancePct}%
          </Typography>
        </Box>
        <Box sx={{ height: "10px", borderRadius: "6px", backgroundColor: "#E5E7EB", overflow: "hidden" }}>
          <Box
            sx={{
              height: "100%",
              width: `${attendancePct}%`,
              background: attendancePct >= 75 ? "linear-gradient(90deg,#4caf50,#81c784)" : "linear-gradient(90deg,#f44336,#ef9a9a)",
              borderRadius: "6px",
              transition: "width 0.6s ease",
            }}
          />
        </Box>
        <Box sx={{ display: "flex", gap: 3, mt: 1.5, flexWrap: "wrap" }}>
          <Typography sx={{ fontSize: "12px", color: "#6B7280" }}>
            Leave %: <strong style={{ color: "#ff9800" }}>{leavePct}%</strong>
          </Typography>
          <Typography sx={{ fontSize: "12px", color: "#6B7280" }}>
            Absent %: <strong style={{ color: "#f44336" }}>{totalDays > 0 ? Math.round((absent / totalDays) * 100) : 0}%</strong>
          </Typography>
        </Box>
      </Paper>

      {/* Calendar */}
      <Paper elevation={0} sx={{ borderRadius: 3, overflow: "hidden", boxShadow: "0 2px 10px rgba(0,0,0,0.06)" }}>
        {/* Calendar header */}
        <Box sx={{ background: "linear-gradient(135deg, #244C77 0%, #4A7BA8 100%)", px: 3, py: 2 }}>
          <Typography sx={{ fontWeight: "bold", fontSize: "16px", color: "#fff" }}>
            {MONTHS[selectedMonth]} {selectedYear}
          </Typography>
        </Box>

        {/* Day-of-week labels */}
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", backgroundColor: "#F9FAFB", borderBottom: "1px solid #E5E7EB" }}>
          {DAY_LABELS.map((day) => (
            <Box key={day} sx={{ textAlign: "center", py: 1 }}>
              <Typography sx={{ fontSize: "12px", fontWeight: 600, color: day === "Sun" || day === "Sat" ? "#9CA3AF" : "#374151" }}>
                {day}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Calendar days grid */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            gap: 0,
            p: 2,
            rowGap: 1,
          }}
        >
          {calendarCells.map((day, idx) => {
            if (!day) {
              return <Box key={`empty-${idx}`} />;
            }
            const weekend = isWeekend(day);
            const status = attendanceMap[day];
            const cfg = status ? STATUS_COLORS[status] : null;
            const todayMark = isToday(day);

            return (
              <Box
                key={day}
                sx={{
                  textAlign: "center",
                  py: 0.8,
                  px: 0.5,
                  borderRadius: "10px",
                  backgroundColor: cfg ? cfg.bg : weekend ? "#F9FAFB" : "transparent",
                  border: todayMark ? "2px solid #244C77" : cfg ? `1px solid ${cfg.border}` : "1px solid transparent",
                  cursor: "default",
                  transition: "background 0.2s",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "13px",
                    fontWeight: todayMark ? 700 : 500,
                    color: cfg ? cfg.text : weekend ? "#9CA3AF" : "#374151",
                  }}
                >
                  {day}
                </Typography>
                {status && (
                  <Typography sx={{ fontSize: "9px", fontWeight: 600, color: cfg.text, lineHeight: 1.2, mt: 0.2 }}>
                    {status === "Present" ? "P" : status === "Absent" ? "A" : "L"}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>

        {/* Legend */}
        <Box sx={{ display: "flex", gap: 2, px: 3, py: 1.5, borderTop: "1px solid #F3F4F6", flexWrap: "wrap" }}>
          {Object.entries(STATUS_COLORS).map(([label, cfg]) => (
            <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "3px",
                  backgroundColor: cfg.bg,
                  border: `1px solid ${cfg.border}`,
                }}
              />
              <Typography sx={{ fontSize: "11px", color: "#6B7280" }}>{label}</Typography>
            </Box>
          ))}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: "3px", border: "2px solid #244C77" }} />
            <Typography sx={{ fontSize: "11px", color: "#6B7280" }}>Today</Typography>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default ParentAttendance;
